import React, { useEffect } from "react";

import {
    Box,
    Flex,
    Text,
    Divider,
    SimpleGrid,
    Icon,
} from "@chakra-ui/react";

import {
    Award,
    TrendingUp,
    Package,
    GraduationCap,
    Star,
    CalendarDays,
    CheckCircle,
    Clock,
    ShieldCheck,
    ClipboardCheck,
    ArrowRight,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import GradientButton from "components/Buttons/GradientButton";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";
import { fetchAgentProgression } from "state/actions/agentProgressionActions";


const ProgressCard = ({
    icon,
    label,
    value,
    description,
}) => {
    return (
        <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            p={5}
            bg="white"
            transition="all 0.2s"
            _hover={{
                borderColor: "gray.300",
                transform: "translateY(-1px)",
            }}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                mb={4}
            >
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    width="42px"
                    height="42px"
                    borderRadius="12px"
                    bg="gray.100"
                >
                    <Icon
                        as={icon}
                        boxSize={5}
                        color="gray.600"
                    />
                </Flex>
            </Flex>

            <Text
                fontSize="xs"
                color="gray.500"
                mb={1}
            >
                {label}
            </Text>

            <Text
                fontSize="xl"
                fontWeight="800"
                color="gray.800"
            >
                {value}
            </Text>

            {description && (
                <Text
                    fontSize="xs"
                    color="gray.500"
                    mt={1}
                >
                    {description}
                </Text>
            )}
        </Box>
    );
};


const ViewAgentProgression = ({
    agent,
    onClose,
}) => {
    const dispatch = useDispatch();

    const {
        agentProgression,
        agentProgressionLoading,
        agentProgressionError,
    } = useSelector(
        (state) => state.agentProgression
    );

    const agentId =
        agent?.agent?.agent_id ||
        agent?.agent?.id ||
        agent?.agent_id ||
        agent?.id;

    useEffect(() => {
        if (agentId) {
            dispatch(
                fetchAgentProgression(
                    agentId
                )
            );
        }
    }, [
        dispatch,
        agentId,
    ]);

    const progression =
        agentProgression;

    const formatDate = (
        date
    ) => {
        if (!date) {
            return "N/A";
        }

        return new Date(
            date
        ).toLocaleString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }
        );
    };


    if (
        agentProgressionLoading
    ) {
        return (
            <Flex
                direction="column"
                p={8}
                width="100%"
                alignItems="center"
                justifyContent="center"
                minHeight="450px"
            >
                <Clock
                    size={32}
                />

                <Text
                    mt={4}
                    fontSize="md"
                    color="gray.500"
                >
                    Loading agent progression...
                </Text>
            </Flex>
        );
    }


    if (
        agentProgressionError
    ) {
        return (
            <Flex
                direction="column"
                p={8}
                width="100%"
                alignItems="center"
                justifyContent="center"
                minHeight="450px"
                textAlign="center"
            >
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    width="70px"
                    height="70px"
                    borderRadius="full"
                    bg="gray.100"
                    mb={5}
                >
                    <ClipboardCheck
                        size={32}
                    />
                </Flex>

                <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color="gray.800"
                    mb={2}
                >
                    Progression Not Available
                </Text>

                <Text
                    fontSize="sm"
                    color="gray.500"
                    maxWidth="380px"
                    lineHeight="1.7"
                    mb={6}
                >
                    {agent?.agent?.status ===
                    "Approved"
                        ? "This agent must be Active before an agent progression record can be viewed."
                        : agentProgressionError}
                </Text>

                <GradientButton
                    onClick={onClose}
                >
                    Close
                </GradientButton>
            </Flex>
        );
    }


    if (!progression) {
        return (
            <Flex
                direction="column"
                p={8}
                width="100%"
                alignItems="center"
                justifyContent="center"
                minHeight="450px"
            >
                <Text
                    color="gray.500"
                >
                    No progression information available.
                </Text>
            </Flex>
        );
    }


    return (
        <Flex
            direction="column"
            p={8}
            width="100%"
        >

            {/* ========================================= */}
            {/* CURRENT PROGRESSION                       */}
            {/* ========================================= */}

            <Box
                borderRadius="2xl"
                p={6}
                bgGradient="linear(to-r, blue.900, blue.700)"
                color="white"
                boxShadow="xl"
            >
                <Flex
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={4}
                >
                    <Box>
                        <Flex
                            alignItems="center"
                            gap={2}
                            mb={3}
                        >
                            <Award
                                size={22}
                            />

                            <Text
                                fontSize="xs"
                                textTransform="uppercase"
                                letterSpacing="1px"
                                opacity={0.8}
                            >
                                Current Level
                            </Text>
                        </Flex>

                        <Text
                            fontSize="2xl"
                            fontWeight="900"
                        >
                            {
                                progression.current_level ||
                                "N/A"
                            }
                        </Text>
                    </Box>

                    <Box
                        textAlign="right"
                    >
                        <Text
                            fontSize="xs"
                            opacity={0.7}
                            mb={2}
                        >
                            Status
                        </Text>

                        <StatusBadge
                            label={
                                progression.status ||
                                "Unknown"
                            }
                            variant={getStatusVariant(
                                progression.status
                            )}
                        />
                    </Box>
                </Flex>

                <Divider
                    my={5}
                    borderColor="whiteAlpha.300"
                />

                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box>
                        <Text
                            fontSize="xs"
                            opacity={0.7}
                            mb={1}
                        >
                            Next Level
                        </Text>

                        <Text
                            fontSize="lg"
                            fontWeight="700"
                        >
                            {
                                progression.next_level ||
                                "N/A"
                            }
                        </Text>
                    </Box>

                    <Flex
                        alignItems="center"
                        justifyContent="center"
                        width="42px"
                        height="42px"
                        borderRadius="full"
                        bg="whiteAlpha.200"
                    >
                        <ArrowRight
                            size={20}
                        />
                    </Flex>
                </Flex>
            </Box>


            {/* ========================================= */}
            {/* ELIGIBILITY                               */}
            {/* ========================================= */}

            <Box mt={6}>
                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={3}
                >
                    Progression Eligibility
                </Text>

                <Box
                    border="1px solid"
                    borderColor={
                        progression.eligible_for_next_level
                            ? "green.200"
                            : "gray.200"
                    }
                    borderRadius="xl"
                    p={5}
                    bg={
                        progression.eligible_for_next_level
                            ? "green.50"
                            : "gray.50"
                    }
                >
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Flex
                            alignItems="center"
                            gap={4}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                width="48px"
                                height="48px"
                                borderRadius="full"
                                bg={
                                    progression.eligible_for_next_level
                                        ? "green.100"
                                        : "gray.200"
                                }
                            >
                                <CheckCircle
                                    size={24}
                                />
                            </Flex>

                            <Box>
                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                >
                                    Eligibility Status
                                </Text>

                                <Text
                                    fontSize="lg"
                                    fontWeight="800"
                                    color="gray.800"
                                >
                                    {
                                        progression.eligible_for_next_level
                                            ? "Eligible for Next Level"
                                            : "Not Eligible Yet"
                                    }
                                </Text>
                            </Box>
                        </Flex>
                    </Flex>
                </Box>
            </Box>


            <Divider my={6} />


            {/* ========================================= */}
            {/* PERFORMANCE                               */}
            {/* ========================================= */}

            <Box>
                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={3}
                >
                    Performance Overview
                </Text>

                <SimpleGrid
                    columns={{
                        base: 1,
                        md: 2,
                    }}
                    spacing={4}
                >

                    <ProgressCard
                        icon={Clock}
                        label="Experience"
                        value={
                            `${
                                progression.experience_months ??
                                0
                            } months`
                        }
                        description="Total agent experience"
                    />

                    <ProgressCard
                        icon={Package}
                        label="Package Volume"
                        value={
                            progression.package_volume ??
                            0
                        }
                        description="Total packages handled"
                    />

                    <ProgressCard
                        icon={Star}
                        label="Customer Rating"
                        value={
                            progression.average_customer_rating ??
                            "N/A"
                        }
                        description="Average customer rating"
                    />

                    <ProgressCard
                        icon={ShieldCheck}
                        label="Training"
                        value={
                            progression.training_completed
                                ? "Completed"
                                : "Incomplete"
                        }
                        description="Training completion status"
                    />

                </SimpleGrid>
            </Box>


            <Divider my={6} />


            {/* ========================================= */}
            {/* TRAINING                                   */}
            {/* ========================================= */}

            <Box mb={6}>
                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={3}
                >
                    Training & Requirements
                </Text>

                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={5}
                    bg="white"
                >
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Flex
                            alignItems="center"
                            gap={4}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                width="46px"
                                height="46px"
                                borderRadius="12px"
                                bg="gray.100"
                            >
                                <GraduationCap
                                    size={23}
                                />
                            </Flex>

                            <Box>
                                <Text
                                    fontSize="sm"
                                    fontWeight="600"
                                    color="gray.800"
                                >
                                    Training Completion
                                </Text>

                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    mt={1}
                                >
                                    Agent training requirement
                                </Text>
                            </Box>
                        </Flex>

                        <Box
                            px={3}
                            py={2}
                            borderRadius="lg"
                            bg={
                                progression.training_completed
                                    ? "green.50"
                                    : "gray.100"
                            }
                        >
                            <Text
                                fontSize="sm"
                                fontWeight="700"
                                color={
                                    progression.training_completed
                                        ? "green.600"
                                        : "gray.600"
                                }
                            >
                                {
                                    progression.training_completed
                                        ? "Completed"
                                        : "Incomplete"
                                }
                            </Text>
                        </Box>
                    </Flex>
                </Box>
            </Box>


            {/* ========================================= */}
            {/* EVALUATION                                */}
            {/* ========================================= */}

            <Box mb={6}>
                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={3}
                >
                    Evaluation
                </Text>

                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={5}
                    bg="white"
                >
                    <Flex
                        alignItems="center"
                        gap={3}
                        mb={4}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            width="40px"
                            height="40px"
                            borderRadius="10px"
                            bg="gray.100"
                        >
                            <ClipboardCheck
                                size={20}
                            />
                        </Flex>

                        <Text
                            fontWeight="600"
                            color="gray.800"
                        >
                            Evaluation Details
                        </Text>
                    </Flex>

                    <Flex
                        justifyContent="space-between"
                        mb={4}
                        gap={4}
                    >
                        <Text
                            fontSize="sm"
                            color="gray.500"
                        >
                            Last Evaluated
                        </Text>

                        <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            textAlign="right"
                        >
                            {formatDate(
                                progression.last_evaluated_at
                            )}
                        </Text>
                    </Flex>

                    <Divider />

                    <Box mt={4}>
                        <Text
                            fontSize="sm"
                            color="gray.500"
                            mb={2}
                        >
                            Evaluation Notes
                        </Text>

                        <Text
                            fontSize="sm"
                            color="gray.700"
                            lineHeight="1.7"
                        >
                            {
                                progression.evaluation_notes ||
                                "No evaluation notes available."
                            }
                        </Text>
                    </Box>
                </Box>
            </Box>


            {/* ========================================= */}
            {/* RECORD INFORMATION                         */}
            {/* ========================================= */}

            <Box>
                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={3}
                >
                    Progression Record
                </Text>

                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={5}
                    bg="white"
                >
                    

                    <Divider />

                    <Flex
                        justifyContent="space-between"
                        gap={4}
                        my={4}
                    >
                        <Text
                            fontSize="sm"
                            color="gray.500"
                        >
                            Created At
                        </Text>

                        <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            textAlign="right"
                        >
                            {formatDate(
                                progression.created_at
                            )}
                        </Text>
                    </Flex>

                    <Divider />

                    <Flex
                        justifyContent="space-between"
                        gap={4}
                        mt={4}
                    >
                        <Text
                            fontSize="sm"
                            color="gray.500"
                        >
                            Updated At
                        </Text>

                        <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            textAlign="right"
                        >
                            {formatDate(
                                progression.updated_at
                            )}
                        </Text>
                    </Flex>
                </Box>
            </Box>


            {/* ========================================= */}
            {/* CLOSE                                     */}
            {/* ========================================= */}

            <Flex
                justifyContent="flex-end"
                mt={6}
            >
                <GradientButton
                    onClick={onClose}
                    height="30px"
                >
                    Close
                </GradientButton>
            </Flex>

        </Flex>
    );
};

export default ViewAgentProgression;